export const PROTOCOL_VERSION = "bianhua.session.v1" as const;

export type ProtocolVersion = typeof PROTOCOL_VERSION;

export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };
export type JsonObject = { [key: string]: JsonValue };

export type Cursor = number;

/**
 * The sole transport envelope used by the browser, desktop client, and gateway.
 * Unknown `kind` values are deliberately representable for forward compatibility.
 */
export interface SessionEnvelope<
  TKind extends string = string,
  TPayload extends JsonValue = JsonValue,
> {
  protocol: ProtocolVersion;
  id: string;
  kind: TKind;
  sentAt: string;
  cursor?: Cursor;
  replyTo?: string;
  payload: TPayload;
}

export const KNOWN_ENVELOPE_KINDS = [
  "connection.hello",
  "connection.welcome",
  "connection.heartbeat",
  "connection.resume",
  "connection.resumed",
  "rpc.request",
  "rpc.response",
  "domain.event",
  "elicitation.request",
  "elicitation.response",
  "artifact.manifest",
  "artifact.chunk",
  "artifact.url",
  "capabilities.snapshot",
] as const;

export type KnownEnvelopeKind = (typeof KNOWN_ENVELOPE_KINDS)[number];

export interface ClientDescriptor extends JsonObject {
  clientId: string;
  name: string;
  version: string;
  platform: "web" | "windows" | "android";
}

export interface ConnectionHelloPayload extends JsonObject {
  client: ClientDescriptor;
  supportedProtocols: ProtocolVersion[];
  lastSessionId?: string;
}

export interface ConnectionWelcomePayload extends JsonObject {
  connectionId: string;
  sessionId: string;
  /** Stable opaque hash of the authenticated principal; never an email or provider token. */
  principalId?: string;
  selectedProtocol: ProtocolVersion;
  serverTime: string;
  heartbeatIntervalMs: number;
  maxMessageBytes: number;
  resumeSupported: boolean;
}

export interface ConnectionHeartbeatPayload extends JsonObject {
  nonce: string;
  direction: "ping" | "pong";
  lastReceivedCursor?: Cursor;
}

export interface ConnectionResumePayload extends JsonObject {
  sessionId: string;
  clientId: string;
  afterCursor?: Cursor;
  previousConnectionId?: string;
}

export interface ConnectionResumedPayload extends JsonObject {
  sessionId: string;
  resetRequired: boolean;
  replayedThroughCursor?: Cursor;
  reason?: string;
}

export interface RpcRequestPayload extends JsonObject {
  method: string;
  params?: JsonValue;
}

export interface RpcErrorPayload extends JsonObject {
  code: string;
  message: string;
  retryable: boolean;
  details?: JsonValue;
}

export type RpcResponsePayload =
  | { result: JsonValue; error?: never }
  | { result?: never; error: RpcErrorPayload };

export const KNOWN_DOMAIN_EVENT_TYPES = [
  "session.ready",
  "session.state",
  "turn.started",
  "turn.progress",
  "turn.completed",
  "message.started",
  "message.delta",
  "message.completed",
  "approval.requested",
  "approval.resolved",
  "artifact.available",
  "video.task.created",
  "video.task.updated",
  "video.task.completed",
  "video.task.cancelled",
  "video.task.failed",
  "capabilities.changed",
  "error",
] as const;

export type KnownDomainEventType = (typeof KNOWN_DOMAIN_EVENT_TYPES)[number];

export interface DomainEventPayload<TData extends JsonValue = JsonValue>
  extends JsonObject {
  eventId: string;
  type: string;
  occurredAt: string;
  data: TData;
}

export const VIDEO_MODEL_IDS = [
  "grok-imagine-video",
  "doubao-seedance-2-0-260128",
  "doubao-seedance-2-0-fast-260128",
  "doubao-seedance-2-5-260628",
] as const;
export const SEEDANCE_MODEL_IDS = VIDEO_MODEL_IDS;

export type VideoModelId = (typeof VIDEO_MODEL_IDS)[number];
/** @deprecated Use VideoModelId. Kept while older persisted Seedance tasks drain. */
export type SeedanceModelId = VideoModelId;

export const VIDEO_TASK_STATUSES = [
  "queued",
  "running",
  "cancelled",
  "succeeded",
  "failed",
  "expired",
] as const;

export type VideoTaskStatus = (typeof VIDEO_TASK_STATUSES)[number];

export interface VideoTaskCreatedData extends JsonObject {
  taskId: string;
  requestId: string;
  provider: "volcengine_ark" | "xai";
  model: VideoModelId;
  status: "queued";
}

export interface VideoTaskUpdatedData extends JsonObject {
  taskId: string;
  status: "queued" | "running" | "expired";
  label?: string;
}

export interface VideoTaskCompletedData extends JsonObject {
  taskId: string;
  status: "succeeded";
  artifactId: string;
}

export interface VideoTaskCancelledData extends JsonObject {
  taskId: string;
  status: "cancelled";
}

export interface VideoTaskFailedData extends JsonObject {
  taskId: string;
  status: "failed" | "expired";
  error: RpcErrorPayload;
}

export interface ServerElicitationPayload extends JsonObject {
  requestId: string;
  source: "codex_app_server" | "gateway";
  method: string;
  category:
    | "approval"
    | "user_input"
    | "mcp_elicitation"
    | "tool_call"
    | "auth_refresh"
    | "attestation";
  params: JsonValue;
  title?: string;
  expiresAt?: string;
}

export interface ElicitationResponsePayload extends JsonObject {
  outcome: "accepted" | "declined" | "cancelled" | "expired" | "error";
  result?: JsonValue;
  error?: RpcErrorPayload;
}

export type DurableElicitationEnvelope = SessionEnvelope<
  "elicitation.request",
  ServerElicitationPayload
> & { cursor: Cursor };

export interface ArtifactRetention extends JsonObject {
  serverStored: boolean;
  objectKey?: string;
  retainedUntil?: string;
}

export interface ChunkTransferDescriptor extends JsonObject {
  mode: "chunks";
  chunkSize: number;
  chunkCount: number;
}

export interface UrlTransferDescriptor extends JsonObject {
  mode: "url";
}

export type ArtifactTransferDescriptor =
  | ChunkTransferDescriptor
  | UrlTransferDescriptor;

export interface ArtifactManifestPayload extends JsonObject {
  artifactId: string;
  projectId: string;
  fileName: string;
  relativePath: string;
  mediaType: string;
  size: number;
  sha256: string;
  transfer: ArtifactTransferDescriptor;
  retention: ArtifactRetention;
  createdAt: string;
}

export interface ArtifactChunkPayload extends JsonObject {
  artifactId: string;
  index: number;
  offset: number;
  dataBase64: string;
  chunkSha256: string;
  final: boolean;
}

export interface ArtifactUrlPayload extends JsonObject {
  artifactId: string;
  url: string;
  expiresAt: string;
  sha256: string;
}

export type CapabilityStatus =
  | "available"
  | "unavailable"
  | "auth_required"
  | "degraded"
  | "experimental";

export type CapabilitySource =
  | "codex_app_server"
  | "openai_api"
  | "provider_api"
  | "gateway_extension"
  | "none";

export interface ModeCapability extends JsonObject {
  mode: "chat" | "work" | "video";
  status: CapabilityStatus;
  source: CapabilitySource;
  reason?: string;
}

export interface FeatureCapability extends JsonObject {
  id: string;
  status: CapabilityStatus;
  source: CapabilitySource;
  operations: string[];
  reason?: string;
}

export interface CodexAppServerCapability extends JsonObject {
  codexCliVersion: string;
  schemaGeneratedWithoutExperimental: boolean;
  clientRequests: string[];
  serverNotifications: string[];
  reverseRequests: string[];
}

export interface CapabilitiesSnapshotPayload extends JsonObject {
  snapshotId: string;
  generatedAt: string;
  modes: {
    chat: ModeCapability;
    work: ModeCapability;
    video?: ModeCapability;
  };
  features: FeatureCapability[];
  codexAppServer?: CodexAppServerCapability;
}

export type KnownEnvelope =
  | SessionEnvelope<"connection.hello", ConnectionHelloPayload>
  | SessionEnvelope<"connection.welcome", ConnectionWelcomePayload>
  | SessionEnvelope<"connection.heartbeat", ConnectionHeartbeatPayload>
  | SessionEnvelope<"connection.resume", ConnectionResumePayload>
  | SessionEnvelope<"connection.resumed", ConnectionResumedPayload>
  | SessionEnvelope<"rpc.request", RpcRequestPayload>
  | SessionEnvelope<"rpc.response", RpcResponsePayload>
  | SessionEnvelope<"domain.event", DomainEventPayload>
  | DurableElicitationEnvelope
  | SessionEnvelope<"elicitation.response", ElicitationResponsePayload>
  | SessionEnvelope<"artifact.manifest", ArtifactManifestPayload>
  | SessionEnvelope<"artifact.chunk", ArtifactChunkPayload>
  | SessionEnvelope<"artifact.url", ArtifactUrlPayload>
  | SessionEnvelope<"capabilities.snapshot", CapabilitiesSnapshotPayload>;

export type UnknownEnvelope = SessionEnvelope<string, JsonValue>;

export interface EnvelopeClassification {
  envelope: SessionEnvelope;
  knownKind: boolean;
  knownDomainEvent: boolean | null;
}
